import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ArrowLeft,
  Clock,
  Star,
  BookOpen,
  Edit3,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Trophy,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Coins,
  Eye,
  Printer,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/comprehensionGame.css";
import { API_BASE, DEV_MODE } from "../config";
import gameConfig from "../data/gameConfig.json";
import { calcTimeBonusPct } from "../utils/scoring";
import apiFetch from "../apiFetch";

const MODE_CFG = gameConfig.comprehension;

const COMP_CACHE_KEY = (tier, topic) => `comprehension_cache_tier_${tier}_topic_${topic}`;

const getCachedGameData = (tier, topic) => {
  try {
    const raw = localStorage.getItem(COMP_CACHE_KEY(tier, topic));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setCachedGameData = (tier, topic, data) => {
  try {
    localStorage.setItem(COMP_CACHE_KEY(tier, topic), JSON.stringify(data));
  } catch {}
};

const clearCachedGameData = (tier, topic) => {
  localStorage.removeItem(COMP_CACHE_KEY(tier, topic));
};
const ComprehensionGame = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Game state management
  useEffect(() => { document.title = "Comprehension Game — Owllet Reading Quest"; }, []);

  const [gameState, setGameState] = useState("loading"); // loading, playing, reviewing, finished
  const [selectedTier] = useState(location.state?.tier || "1");
  const [selectedTierName] = useState(location.state?.tierName || "Novice");
  const [selectedTopicCategory] = useState(location.state?.topicCategory || "Adventure");
  const [passage, setPassage] = useState("");
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(MODE_CFG.time);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [markingResults, setMarkingResults] = useState(null);
  const [coinsEarned, setCoinsEarned] = useState(0);
  const [reviewMode, setReviewMode] = useState(false);
  const [gameFilename, setGameFilename] = useState(null);
  const [gameLevel, setGameLevel] = useState(null);
  const [currentReviewQuestion, setCurrentReviewQuestion] = useState(0);
  const [testMode, setTestMode] = useState(false);
  const [showTestModeModal, setShowTestModeModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const testModeModalRef = useRef(null);
  const submitModalRef = useRef(null);

  // Focus trap + Escape key for modals
  const trapFocus = useCallback((ref, onClose) => {
    const el = ref.current;
    if (!el) return;
    const focusable = el.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();
    const handler = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key !== "Tab") return;
      if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last?.focus(); } }
      else { if (document.activeElement === last) { e.preventDefault(); first?.focus(); } }
    };
    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (showTestModeModal) return trapFocus(testModeModalRef, () => setShowTestModeModal(false));
  }, [showTestModeModal, trapFocus]);

  useEffect(() => {
    if (showSubmitModal) return trapFocus(submitModalRef, () => setShowSubmitModal(false));
  }, [showSubmitModal, trapFocus]);

  const MODE_KEY = "comprehension";
  const hasPostedRef = React.useRef(false);
  const hasInitRef = React.useRef(false);

  // Initialize game
  useEffect(() => {
    if (hasInitRef.current) return;
    hasInitRef.current = true;
    initializeGame(location.state?.tier || "1", location.state?.topicCategory || "Adventure", location.state?.tierName || "Novice");
  }, []);

  // Timer logic
  useEffect(() => {
    if (DEV_MODE) return;
    if (gameState === "playing" && timeLeft > 0 && !testMode) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameState === "playing" && !testMode) {
      handleSubmit();
    }
  }, [timeLeft, gameState, testMode]);

  const initializeGame = async (tier = "1", topicCategory = "Adventure", tierName = "Novice") => {
    setTestMode(false);
    setLoading(true);
    setError("");

    try {
      let data = getCachedGameData(tier, topicCategory);
      if (data && data.level !== parseInt(tier, 10)) data = null;

      if (!data) {
        const userId = parseInt(localStorage.getItem("userId"), 10);
        const response = await apiFetch(`/api/generate-comprehension`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, tier, tierName, topicCategory }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        data = await response.json();

        if (!data.passage || !data.questions || data.questions.length !== 6) {
          throw new Error("Invalid data received from API");
        }

        setCachedGameData(tier, topicCategory, data);
      }

      setGameFilename(data.filename || null);
      setGameLevel(data.level ?? null);
      setPassage(data.passage);
      setQuestions(data.questions);

      // Initialize user answers
      const initialAnswers = {};
      data.questions.forEach((_, index) => {
        initialAnswers[index] = "";
      });
      setUserAnswers(initialAnswers);

      setGameState("playing");
      setTimeLeft(600);
    } catch (error) {
      console.error("Error fetching game data:", error);
      navigate("/error");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionIndex, answer) => {
    setUserAnswers((prev) => ({
      ...prev,
      [questionIndex]: answer,
    }));
  };

  const handleSubmit = async (isExplicit = false) => {
    setLoading(true);
    setGameState("reviewing");

    try {
      const response = await apiFetch(`/api/mark-comprehension`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          questions: questions,
          userAnswers: userAnswers,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const results = await response.json();
      setMarkingResults(results);

      // Coins: 2 coins per mark, + up to 50% time bonus
      const maxScore = results?.maxScore ?? getTotalMarks();
      const coins = calculateCoins(results.totalScore || 0, maxScore, timeLeft);
      setCoinsEarned(testMode ? 0 : coins);

      clearCachedGameData(selectedTier, selectedTopicCategory);
      if (isExplicit && !testMode) {
        await addCoinsAndXPToUser(coins);
        await saveNonRankedSession((results.totalScore || 0) * 5);
      }

      const userId = parseInt(localStorage.getItem("userId"), 10);
      if (userId && gameFilename && gameLevel != null) {
        apiFetch(`/api/mark-comprehension-played`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, filename: gameFilename, level: gameLevel }),
        }).catch(() => {});
      }

      if (userId) {
        apiFetch(`/api/results/save`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId,
            gameType: "comprehension",
            title: `${selectedTopicCategory} — Tier ${selectedTierName}`,
            score: results.totalScore ?? 0,
            maxScore: results.maxScore ?? getTotalMarks(),
            resultJson: { passage, questions, userAnswers, markingResults: results },
          }),
        }).catch(() => {});
      }

      setGameState("finished");
    } catch (error) {
      console.error("Error marking answers:", error);
      setError("Failed to mark answers. Please try again.");
      setGameState("playing");
    } finally {
      setLoading(false);
    }
  };

  const navigateToQuestion = (index) => {
    setCurrentQuestion(index);
  };

  const buildReviewHTML = (filename) => {
    const topicLabel = selectedTopicCategory;
    const rows = questions.map((q, i) => {
      const result = markingResults?.results?.[i] || {};
      return `
        <div class="q-block">
          <div class="q-header">
            <span class="q-num">Question ${i + 1}</span>
            <span class="q-marks">${result.marksAwarded ?? "—"} / ${q.marks ?? "?"} marks</span>
          </div>
          <p class="q-text">${q.question}</p>
          <div class="section">
            <div class="section-label">Your Answer</div>
            <div class="answer-box">${userAnswers[i] || "<em>No answer provided</em>"}</div>
          </div>
          <div class="section">
            <div class="section-label">Explanation</div>
            <p>${result.explanation || "—"}</p>
          </div>
          <div class="section feedback-section">
            <div class="section-label">Feedback</div>
            <p>${result.feedback || "—"}</p>
          </div>
        </div>`;
    }).join("");

    return `<!DOCTYPE html><html><head>
      <meta charset="utf-8"/>
      <title>${filename}</title>
      <style>
        body { font-family: Georgia, serif; color: #1f2937; margin: 0; padding: 32px; font-size: 13px; }
        h1 { font-size: 20px; margin: 0 0 4px; color: #111; }
        .meta { font-size: 12px; color: #6b7280; margin-bottom: 24px; }
        h2 { font-size: 15px; margin: 0 0 10px; color: #374151; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; }
        .passage { background: #f9fafb; border-left: 3px solid #6366f1; padding: 14px 16px; border-radius: 4px; line-height: 1.7; margin-bottom: 32px; white-space: pre-wrap; }
        .q-block { border: 1px solid #e5e7eb; border-radius: 6px; padding: 16px; margin-bottom: 16px; break-inside: avoid; }
        .q-header { display: flex; justify-content: space-between; margin-bottom: 8px; }
        .q-num { font-weight: 700; color: #4f46e5; }
        .q-marks { font-weight: 600; color: #059669; }
        .q-text { font-size: 13px; margin: 0 0 12px; line-height: 1.5; }
        .section { margin-top: 10px; }
        .section-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #9ca3af; margin-bottom: 4px; }
        .answer-box { background: #f3f4f6; border-radius: 4px; padding: 10px 12px; line-height: 1.5; white-space: pre-wrap; }
        .feedback-section p { color: #065f46; }
        @media print { body { padding: 16px; } }
      </style>
    </head><body>
      <h1>${topicLabel} — Comprehension Review</h1>
      <p class="meta">Level ${selectedTier} (${selectedTierName}) &nbsp;·&nbsp; ${filename}</p>
      <h2>Reading Passage</h2>
      <div class="passage">${passage}</div>
      <h2>Questions &amp; Answers</h2>
      ${rows}
    </body></html>`;
  };

  const getPdfFilename = () => {
    const topicSlug = selectedTopicCategory.toLowerCase().replace(/[^a-z0-9]/g, "_");
    return `${topicSlug}_comp_L${String(selectedTier).padStart(4, "0")}`;
  };

  const handlePrint = () => {
    const filename = getPdfFilename();
    const html = buildReviewHTML(filename);
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    win.document.title = filename;
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getTotalMarks = () => {
    return questions.reduce((total, question) => total + question.marks, 0);
  };

  //===========================
  //Game save methods for stats
  //===========================

  const saveNonRankedSession = async (score) => {
    if (hasPostedRef.current) return;
    hasPostedRef.current = true;

    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId || Number.isNaN(userId)) return;

    try {
      const res = await apiFetch(`/api/sessions/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, mode: MODE_KEY, score }),
        keepalive: true,
      });

      if (!res.ok) {
        console.error("Session save failed:", res.status, await res.text());
      }
    } catch (err) {
      console.error("Network error saving session:", err);
    }
  };

  // --- Coins helpers (drop these near the top of the file) ---
  const calculateCoins = (score, maxScore, timeLeftSec) => {
    const accuracy = maxScore > 0 ? score / maxScore : 0;
    const bonusPct = calcTimeBonusPct(accuracy, timeLeftSec, MODE_CFG.time, MODE_CFG.maxTimeBonusPct);
    return Math.floor(score * MODE_CFG.baseCoinsPerMark * (1 + bonusPct) * MODE_CFG.coinsMultiplier);
  };

  const addCoinsAndXPToUser = async (coinsToAdd) => {
    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId || coinsToAdd <= 0) return;

    try {
      // 1) Update coins
      const res = await apiFetch(`/api/resources/update-coins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, delta: coinsToAdd }),
      });
      if (!res.ok) {
        console.error("Failed to update coins:", await res.text());
        return;
      }

      // 2) Update XP (coins × 5)
      const xpRes = await apiFetch(`/api/resources/xp/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, delta: coinsToAdd * MODE_CFG.xpMultiplier }),
      });
      if (!xpRes.ok) {
        console.error("Failed to update XP:", await xpRes.text());
        return;
      }
    } catch (err) {
      console.error("Error updating coins/XP:", err);
    }
  };

  if (gameState === "loading") {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <BookOpen className={`spinner-icon ${loading ? "spinning" : ""}`} aria-hidden="true" />
          <p role="status" aria-live="polite">
            {loading
              ? "Generating your comprehension challenge..."
              : "Preparing your test..."}
          </p>
          {error && (
            <div className="error-message" role="alert">
              <AlertCircle aria-hidden="true" />
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (gameState === "reviewing") {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <Edit3 className="spinner-icon spinning" aria-hidden="true" />
          <p role="status" aria-live="polite">Marking your answers…</p>
          <p className="sub-text">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (gameState === "finished") {
    if (reviewMode) {
      return (
        <div className="review-screen">
          <div className="review-header">
            <button
              className="back-to-results"
              onClick={() => setReviewMode(false)}
            >
              <ArrowLeft /> Back to Results
            </button>
            <h2>Answer Review</h2>
          </div>

          <div className="review-content">
            <div className="passage-review">
              <h3>Reading Passage</h3>
              <div className="passage-text">{passage}</div>
            </div>

            <div className="question-review">
              <div className="review-navigation">
                {markingResults?.results?.map((result, index) => (
                  <button
                    key={index}
                    className={`review-nav-button ${index === currentReviewQuestion ? "active" : ""}`}
                    onClick={() => setCurrentReviewQuestion(index)}
                  >
                    Q{index + 1} · {result.marksAwarded ?? "?"}/{questions[index]?.marks ?? "?"}m
                  </button>
                ))}
                <div className="review-nav-actions">
                  <button className="review-action-btn" onClick={handlePrint} title="Print / Save as PDF">
                    <Printer size={15} />
                  </button>
                </div>
              </div>

              <div className="review-question">
                <h4>
                  Question {currentReviewQuestion + 1}
                  {(() => {
                    const r = markingResults?.results?.[currentReviewQuestion];
                    const total = questions[currentReviewQuestion]?.marks;
                    if (!r || total == null) return null;
                    const awarded = r.marksAwarded ?? 0;
                    const cls = awarded === total ? "full" : awarded === 0 ? "zero" : "partial";
                    return (
                      <span className={`review-marks-badge ${cls}`}>
                        {awarded} / {total} marks
                      </span>
                    );
                  })()}
                </h4>
                <div className="question-text">
                  {questions[currentReviewQuestion]?.question}
                </div>

                <div className="user-answer-display">
                  <strong className="review-label">Your Answer:</strong>
                  <div className="answer-text">
                    {userAnswers[currentReviewQuestion] || "No answer provided"}
                  </div>
                </div>

                <div className="marking-breakdown">
                  <strong className="review-label">Explanation:</strong>
                  <p>
                    {
                      markingResults?.results?.[currentReviewQuestion]
                        ?.explanation
                    }
                  </p>
                </div>

                <div className="feedback">
                  <strong className="review-label">Feedback:</strong>
                  <p>
                    {markingResults?.results?.[currentReviewQuestion]?.feedback}
                  </p>
                </div>
              </div>

              <div className="review-controls">
                <button
                  onClick={() =>
                    setCurrentReviewQuestion(
                      Math.max(0, currentReviewQuestion - 1),
                    )
                  }
                  disabled={currentReviewQuestion === 0}
                  className="nav-button"
                >
                  <ChevronLeft /> Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentReviewQuestion(
                      Math.min(questions.length - 1, currentReviewQuestion + 1),
                    )
                  }
                  disabled={currentReviewQuestion === questions.length - 1}
                  className="nav-button"
                >
                  Next <ChevronRight />
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // results screen (if not reviewing)
    return (
      <div className="results-screen">
        <div className="results-content">
          <Trophy className="trophy-icon" />
          <h2>Test Complete!</h2>

          <div className="final-stats">
            <div className="stat-item">
              <Star className="stat-icon" />
              <div>
                <div className="stat-label">Final Score</div>
                <div className="stat-value">
                  {markingResults?.totalScore || 0}/
                  {markingResults?.maxScore || getTotalMarks()}
                </div>
              </div>
            </div>
            <div className="stat-item">
              <Coins className="stat-icon" />
              <div>
                <div className="stat-label">Coins Earned</div>
                <div className="stat-value">{coinsEarned}</div>
              </div>
            </div>
          </div>

          <div className="action-buttons">
            <button
              className="primary-button"
              onClick={() => setReviewMode(true)}
            >
              <Eye /> Review Answers
            </button>
            <button
              className="secondary-button"
              onClick={() => {
                setCoinsEarned(0);
                initializeGame(selectedTier, selectedTopicCategory, selectedTierName);
              }}
              disabled={loading}
            >
              <RefreshCw className={loading ? "spinning" : ""} />
              New Test
            </button>
            <button
              className="tertiary-button"
              onClick={onBack}
            >
              <ArrowLeft /> Back to Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="comprehension-game game-container">
      <GameHeader
        timeLeft={timeLeft}
        formatTime={formatTime}
        currentQuestion={currentQuestion}
        totalQuestions={questions.length}
        onBack={() => navigate("/games/comprehension")}
        testMode={testMode}
        onTestMode={() => setShowTestModeModal(true)}
      />

      {showTestModeModal && (
        <div className="test-mode-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="test-mode-modal-title">
          <div className="test-mode-modal" ref={testModeModalRef}>
            <div className="test-mode-modal-icon"><Clock size={26} aria-hidden="true" /></div>
            <h3 id="test-mode-modal-title">Enable Learning Mode?</h3>
            <p>The timer will stop and you'll have unlimited time to finish, but <strong>no coins or XP</strong> will be awarded. Your attempt will still be saved to your history.</p>
            <div className="test-mode-modal-actions">
              <button className="test-mode-confirm-btn" onClick={() => { setTestMode(true); setShowTestModeModal(false); }}>
                Confirm — Enable Learning Mode
              </button>
              <button className="test-mode-cancel-btn" onClick={() => setShowTestModeModal(false)}>
                Cancel — Keep Playing Normally
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="error-banner">
          <AlertCircle />
          {error}
        </div>
      )}

      <div className="game-content">
        <PassageSection passage={passage} />

        <div className="questions-section">
          <QuestionNavigation
            questions={questions}
            currentQuestion={currentQuestion}
            userAnswers={userAnswers}
            onNavigate={navigateToQuestion}
          />

          <QuestionPanel
            question={questions[currentQuestion]}
            questionIndex={currentQuestion}
            userAnswer={userAnswers[currentQuestion] || ""}
            onAnswerChange={handleAnswerChange}
            totalQuestions={questions.length}
          />

          <div className="control-buttons">
            <button
              className="nav-button"
              onClick={() =>
                navigateToQuestion(Math.max(0, currentQuestion - 1))
              }
              disabled={currentQuestion === 0}
            >
              <ChevronLeft /> Previous
            </button>

            <button
              className="nav-button"
              onClick={() =>
                navigateToQuestion(
                  Math.min(questions.length - 1, currentQuestion + 1),
                )
              }
              disabled={currentQuestion === questions.length - 1}
            >
              Next <ChevronRight />
            </button>

            <button
              className="submit-button"
              onClick={() => {
                const allAnswered = questions.every((_, i) => userAnswers[i]?.trim().length > 0);
                if (allAnswered || (testMode) || timeLeft <= 0) {
                  handleSubmit(true);
                } else {
                  setShowSubmitModal(true);
                }
              }}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Finish & Submit"}
            </button>
          </div>
        </div>
      </div>

      {showSubmitModal && (
        <div className="submit-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="submit-modal-title">
          <div className="submit-confirm-modal" ref={submitModalRef}>
            <div className="submit-confirm-icon">
              <AlertTriangle size={26} aria-hidden="true" />
            </div>
            <h3 id="submit-modal-title">Unanswered Questions</h3>
            <p>
              You have{" "}
              {questions.filter((_, i) => !userAnswers[i]?.trim().length).length}{" "}
              unanswered question(s). Are you sure you want to submit?
            </p>
            <div className="submit-confirm-actions">
              <button className="submit-confirm-back" onClick={() => setShowSubmitModal(false)}>
                Go Back
              </button>
              <button className="submit-confirm-go" onClick={() => { setShowSubmitModal(false); handleSubmit(true); }}>
                Submit Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-components
const GameHeader = ({
  timeLeft,
  formatTime,
  currentQuestion,
  totalQuestions,
  onBack,
  testMode,
  onTestMode,
}) => (
  <header className="game-header">
    <div className="header-left">
      <h1>Comprehension Game</h1>
    </div>
    <div className="header-right">
      <div className="header-stats">
        <div className="progress-indicator">
          <span>Question {currentQuestion + 1} of {totalQuestions}</span>
        </div>
        <div className={`timer ${!testMode && timeLeft < 120 ? "urgent" : ""}`}>
          <Clock /> {testMode ? "∞" : formatTime(timeLeft)}
        </div>
        {testMode
          ? <span className="test-mode-badge"><Clock size={11} /> Learning Mode</span>
          : <button className="test-mode-btn" onClick={onTestMode}><Clock size={13} /> Learning Mode</button>
        }
      </div>
    </div>
    <button className="header-back-btn" onClick={onBack}>
      <ArrowLeft size={14} /> Back to Dashboard
    </button>
  </header>
);

const PassageSection = ({ passage }) => (
  <div className="passage-section">
    <h3>Reading Passage</h3>
    <div className="passage-text">{passage}</div>
  </div>
);

const QuestionNavigation = ({
  questions,
  currentQuestion,
  userAnswers,
  onNavigate,
}) => (
  <div className="question-navigation">
    <div className="question-tabs">
      {questions.map((question, index) => (
        <button
          key={index}
          className={`question-tab ${index === currentQuestion ? "active" : ""} ${userAnswers[index] ? "answered" : ""}`}
          onClick={() => onNavigate(index)}
        >
          <span className="question-number">{index + 1}</span>
          <span className="question-marks">({question.marks}m)</span>
          {userAnswers[index] && <CheckCircle className="answered-icon" />}
        </button>
      ))}
    </div>
  </div>
);

const QuestionPanel = ({
  question,
  questionIndex,
  userAnswer,
  onAnswerChange,
  totalQuestions,
}) => {
  if (!question) return null;

  return (
    <div className="question-panel">
      <div className="question-header">
        <h4>
          Question {questionIndex + 1} of {totalQuestions}
        </h4>
        <span className="marks-indicator">[{question.marks} marks]</span>
      </div>

      <div className="question-text">{question.question}</div>

      <div className="answer-section">
        <textarea
          className="answer-input"
          value={userAnswer}
          onChange={(e) => onAnswerChange(questionIndex, e.target.value)}
          placeholder="Type your answer here..."
          rows={question.marks > 3 ? 8 : 5}
        />
        <div className="answer-info">
          <span className="character-count">
            {userAnswer.length} characters
          </span>
          {question.markingPoints && (
            <div className="marking-points">
              <strong className="review-label">Marking Points:</strong>
              <ul>
                {question.markingPoints.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComprehensionGame;
